import React, { useEffect, useState } from "react";
import { fetchReportSummary, exportReportsCsv } from "../../../services/aro/reportService";
import { ReportSummary, CertBreakdown } from "../../../types/aro";
import StatsCard      from "../shared/StatsCard/StatsCard";
import LoadingSpinner from "../shared/LoadingSpinner/LoadingSpinner";
import EmptyState     from "../shared/EmptyState/EmptyState";
import PageHeader     from "../../shared/PageHeader/PageHeader";
import "./ReportsPage.css";

function ProgressBar({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="aro-progress">
      <div className="aro-progress__bar" style={{ width: `${pct}%` }} />
      <span className="aro-progress__label">{value.toFixed(1)}%</span>
    </div>
  );
}

export default function ReportsPage() {
  const [summary, setSummary] = useState<ReportSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState<string | null>(null);

  useEffect(() => {
    fetchReportSummary()
      .then(setSummary)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleExport = async () => {
    setExporting(true);
    setExportErr(null);
    try {
      const blob = await exportReportsCsv();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = "aro_report.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setExportErr(e.message);
    } finally {
      setExporting(false);
    }
  };

  const sorted: CertBreakdown[] = summary
    ? [...summary.breakdown_by_cert].sort((a, b) => b.pct_changed - a.pct_changed)
    : [];

  const maxPct = sorted.length > 0 ? Math.max(...sorted.map((d) => d.pct_changed)) : 100;

  return (
    <div className="aro-page">
      <div className="aro-reports-header">
        <PageHeader title="AI Report" subtitle="Job outcome summary across all certificates." />
        <button
          className="aro-btn aro-btn--primary"
          onClick={handleExport}
          disabled={exporting}
        >
          {exporting ? "Exporting…" : "⬇ Export Full Dataset (CSV)"}
        </button>
      </div>

      {exportErr && <div className="aro-error-banner">{exportErr}</div>}
      {error     && <div className="aro-error-banner">{error}</div>}

      {loading ? (
        <LoadingSpinner />
      ) : !summary ? null : (
        <>
          <div className="aro-reports-stats">
            <StatsCard title="Total People Tracked"      value={summary.total_persons}  color="blue"  />
            <StatsCard title="Active Tracking Windows"   value={summary.active_windows} color="amber" />
            <StatsCard title="Company Changes Detected"  value={summary.total_changed}  color="green" />
            <StatsCard title="% Changed Company"         value={`${summary.pct_changed.toFixed(1)}%`} color="gray" />
          </div>

          <div className="aro-card">
            <h2 className="aro-section-title">Breakdown by Certificate</h2>
            {sorted.length === 0 ? (
              <EmptyState icon="📊" message="No breakdown data available." />
            ) : (
              <table className="aro-table">
                <thead>
                  <tr>
                    <th>Certificate Name</th>
                    <th>Total Earners</th>
                    <th>Changes Detected</th>
                    <th style={{ minWidth: 200 }}>% Changed</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((row, i) => (
                    <tr key={row.certificate_name} className={i % 2 === 1 ? "aro-table__row--odd" : ""}>
                      <td className="aro-table__cell--primary">{row.certificate_name}</td>
                      <td>{row.total}</td>
                      <td>{row.changed}</td>
                      <td>
                        <ProgressBar value={row.pct_changed} max={maxPct} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
